import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import SidebarNavigation from './SidebarNavigation';
import TopBar from './TopBar';
import CreateQuizSession from './CreateQuizSession';
import UserManagement from './UserManagement';
import Profile from './Profile';
import Reports from './Reports';
import TrainingDashboard from './TrainingDashboard';
import DiologicSchedulerEmbed from './DiologicSchedulerEmbed';
import ConsultantBookings from './ConsultantBookings';
import { useUserRole } from '../hooks/useUserRole';
import { PATH_TO_SECTION, SECTION_TO_PATH } from '../config/roles';
import { toastManager } from '../utils/toastManager';
import { getSessionsForUser, getSessionsForAdmin, getQuizFiles, getCompletionStats, deleteSession, deleteQuizFile, createInterviewSlot, getAllInterviewSlotsInRange, getMyInterviewSlots, updateInterviewSlot, deleteInterviewSlot, getWorklogsForWeek, getWorklogsForRange, getWeekRange, getMonthRange, toDateString, saveWorklog, buildQuizPlayerUrl } from '../utils/quizSessionManager';
import { getConsultantUsers, getSupportUsers } from '../utils/firebaseAuth';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { BarChart3, CheckCircle2, FileText, ExternalLink, Users, Play, Trash2, Plus, ChevronLeft, ChevronRight, X, ChevronDown } from 'lucide-react';
import './AdminDashboard.css';

// Timezone options for book-slot form (value = IANA)
const TIMEZONE_OPTIONS = [
  { value: 'America/Chicago', label: 'USA (CST)' },
  { value: 'America/New_York', label: 'USA (EST)' },
  { value: 'America/Los_Angeles', label: 'USA (California PST)' },
  { value: 'Asia/Kolkata', label: 'India (Chennai)' },
];
const INTERVIEW_THROUGH_OPTIONS = ['Zoom', 'Microsoft Teams', 'Google Meet', 'Other'];
const DURATION_OPTIONS = [15, 30, 45, 60, 90];
const MODE_OPTIONS = [{ value: 'video', label: 'Video' }, { value: 'audio', label: 'Audio' }];

function CustomSelect({ value, onChange, options = [], placeholder = 'Select…', className = '' }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = React.useRef(null);
  const normalizedOptions = options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt));
  const selectedOption = normalizedOptions.find((opt) => opt.value === value);
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  React.useEffect(() => {
    const handleMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleSelect = (optValue) => { onChange(optValue); setOpen(false); };

  return (
    <div ref={wrapperRef} className={`custom-select ${className}`.trim()}>
      <button type="button" className="custom-select-trigger" onClick={() => setOpen((p) => !p)} aria-expanded={open} aria-haspopup="listbox" aria-label={displayLabel}>
        <span className="custom-select-value">{displayLabel}</span>
        <ChevronDown size={16} className="custom-select-chevron" />
      </button>
      {open && (
        <div className="custom-select-panel" role="listbox">
          {normalizedOptions.map((opt) => (
            <div key={opt.value} role="option" aria-selected={opt.value === value} className="custom-select-option" onClick={() => handleSelect(opt.value)}>
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CalendarSection({ userEmail, isAdmin }) {
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [form, setForm] = useState({
    name: '', timezone: 'America/New_York', email: '', client: '', vendor: '',
    mode: 'video', durationMinutes: 30, interviewThrough: 'Zoom', interviewThroughOther: '',
    date: '', time: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [detailsSlot, setDetailsSlot] = useState(null);
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [cancelConfirmSlotId, setCancelConfirmSlotId] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultantEmail, setSelectedConsultantEmail] = useState('');

  const getTimezoneLabel = (value) => (TIMEZONE_OPTIONS.find(o => o.value === value)?.label) || value || '';

  const time24To12 = (time24) => {
    if (!time24 || !time24.match(/^\d{1,2}:\d{2}$/)) return { hour: 9, minute: 0, amPm: 'AM' };
    const [h, m] = time24.split(':').map(Number);
    const hour24 = Math.min(23, Math.max(0, h));
    const minute = Math.min(59, Math.max(0, m));
    if (hour24 === 0) return { hour: 12, minute, amPm: 'AM' };
    if (hour24 === 12) return { hour: 12, minute, amPm: 'PM' };
    if (hour24 < 12) return { hour: hour24, minute, amPm: 'AM' };
    return { hour: hour24 - 12, minute, amPm: 'PM' };
  };
  const time12To24 = (hour12, minute, amPm) => {
    let h = hour12 == null || hour12 < 1 || hour12 > 12 ? 9 : Number(hour12);
    const m = minute == null || minute < 0 || minute > 59 ? 0 : Math.min(59, Math.max(0, Number(minute)));
    if (amPm === 'PM' && h !== 12) h += 12;
    if (amPm === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i);
  const HOUR_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  useEffect(() => {
    if (isAdmin) {
      getConsultantUsers().then((emails) => {
        setConsultants((emails || []).map((email) => ({ value: email, label: email })));
      });
    }
  }, [isAdmin]);

  const getRangeForView = () => {
    const d = new Date(currentDate);
    if (viewMode === 'month') {
      d.setDate(1);
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
      return { start, end };
    }
    if (viewMode === 'week') {
      const day = d.getDay();
      const start = new Date(d);
      start.setDate(d.getDate() - day);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    const start = new Date(d);
    start.setHours(0, 0, 0, 0);
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const loadSlots = React.useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const { start, end } = getRangeForView();
      const list = await getAllInterviewSlotsInRange(start, end);
      if (isAdmin && selectedConsultantEmail) {
        const email = selectedConsultantEmail.toLowerCase().trim();
        setSlots(list.filter((s) => (s.bookedBy || '').toLowerCase() === email));
      } else {
        setSlots(list);
      }
    } catch (err) {
      console.error('Load slots error:', err);
      toastManager.error('Failed to load calendar slots');
    } finally {
      setLoading(false);
    }
  }, [userEmail, currentDate, viewMode, isAdmin, selectedConsultantEmail]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  useEffect(() => {
    const open = modalOpen || !!detailsSlot || !!cancelConfirmSlotId;
    const prev = document.body.style.overflow;
    if (open) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [modalOpen, detailsSlot, cancelConfirmSlotId]);

  const openBookSlot = (date, time) => {
    setEditingSlotId(null);
    const d = date || new Date();
    setSelectedDate(d);
    setSelectedTime(time || '');
    setForm({
      name: '',
      timezone: 'America/New_York',
      email: '',
      client: '',
      vendor: '',
      mode: 'video',
      durationMinutes: 30,
      interviewThrough: 'Zoom',
      interviewThroughOther: '',
      date: d.toISOString().slice(0, 10),
      time: time || '09:00',
    });
    setErrors({});
    setModalOpen(true);
  };

  const openSlotDetails = (slot) => {
    if (!slot) return;
    if (isAdmin || (slot.bookedBy || '').toLowerCase() === (userEmail || '').toLowerCase()) {
      setDetailsSlot(slot);
    }
  };

  const closeSlotDetails = () => {
    setDetailsSlot(null);
    setCancelConfirmSlotId(null);
  };

  const openReschedule = (slot) => {
    if (!slot) return;
    setEditingSlotId(slot.id);
    const startMs = slot.startTime?.toMillis ? slot.startTime.toMillis() : (slot.startTime && slot.startTime.seconds ? slot.startTime.seconds * 1000 : null);
    const startDate = startMs ? new Date(startMs) : new Date();
    const hours = startDate.getHours();
    const minutes = startDate.getMinutes();
    const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    setForm({
      name: slot.name || '',
      email: slot.email || '',
      vendor: slot.vendor || '',
      client: slot.client || '',
      timezone: slot.timezone || 'America/New_York',
      mode: slot.mode || 'video',
      durationMinutes: slot.durationMinutes ?? 30,
      interviewThrough: slot.interviewThrough || 'Zoom',
      interviewThroughOther: slot.interviewThroughOther || '',
      date: startDate.toISOString().slice(0, 10),
      time: timeStr,
    });
    setErrors({});
    setDetailsSlot(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDate(null);
    setSelectedTime('');
    setErrors({});
    setEditingSlotId(null);
  };

  const handleFormChange = (field, value) => {
    if (field === 'date') {
      const todayStr = new Date().toISOString().slice(0, 10);
      if (value === todayStr) {
        const now = new Date();
        const minMins = Math.ceil((now.getHours() * 60 + now.getMinutes()) / 30) * 30;
        const timeParts = (form.time || '').split(':').map(Number);
        const timeMins = timeParts.length >= 2 ? timeParts[0] * 60 + timeParts[1] : -1;
        if (form.time && timeMins < minMins) {
          setForm(prev => ({ ...prev, date: value, time: '09:00' }));
          setErrors(prev => ({ ...prev, date: '', time: '' }));
          return;
        }
      }
    }
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const { name, email, vendor, client, timezone, date, time, mode, durationMinutes, interviewThrough, interviewThroughOther } = form;
    const next = {};
    if (!(name || '').trim()) next.name = 'Required';
    if (!(email || '').trim()) next.email = 'Required';
    if (!(vendor || '').trim()) next.vendor = 'Required';
    if (!(client || '').trim()) next.client = 'Required';
    if (!(timezone || '').trim()) next.timezone = 'Required';
    if (!(date || '').trim()) next.date = 'Required';
    if (!(time || '').trim()) next.time = 'Required';
    if (!(mode || '').trim()) next.mode = 'Required';
    if (durationMinutes == null || durationMinutes === '') next.durationMinutes = 'Required';
    if (mode === 'video') {
      if (!(interviewThrough || '').trim()) next.interviewThrough = 'Required';
      if (interviewThrough === 'Other' && !(interviewThroughOther || '').trim()) next.interviewThroughOther = 'Required';
    }
    if (date && time) {
      const [h, min] = time.split(':').map(Number);
      const start = new Date(date);
      start.setHours(h, min || 0, 0, 0);
      if (start < new Date()) next.time = 'Cannot select past date or time';
    }
    return next;
  };

  const handleBookSlotSubmit = async (e) => {
    e.preventDefault();
    if (!userEmail) return;
    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    const { name, email, timezone, client, vendor, mode, durationMinutes, interviewThrough, interviewThroughOther, date, time } = form;
    const [hours, minutes] = time.split(':').map(Number);
    const startLocal = new Date(date);
    startLocal.setHours(hours, minutes || 0, 0, 0);
    const endLocal = new Date(startLocal.getTime() + durationMinutes * 60 * 1000);
    const dayStart = new Date(startLocal);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(startLocal);
    dayEnd.setHours(23, 59, 59, 999);
    try {
      const existingInDay = await getAllInterviewSlotsInRange(dayStart, dayEnd);
      const overlaps = existingInDay.some((s) => {
        if (editingSlotId && s.id === editingSlotId) return false;
        const sStart = s.startTime?.toMillis ? s.startTime.toMillis() : (s.startTime?.seconds != null ? s.startTime.seconds * 1000 : 0);
        const sEnd = s.endTime?.toMillis ? s.endTime.toMillis() : (s.endTime?.seconds != null ? s.endTime.seconds * 1000 : 0);
        return startLocal.getTime() < sEnd && endLocal.getTime() > sStart;
      });
      if (overlaps) {
        setErrors((prev) => ({ ...prev, time: 'This time slot is already booked. Please choose another time.' }));
        return;
      }
    } catch (err) {
      console.error('Overlap check error:', err);
    }
    const title = client ? `Interview – ${client}` : 'Interview';
    const payload = {
      bookedBy: userEmail,
      startTime: startLocal,
      endTime: endLocal,
      timezone,
      name: name.trim(),
      email: email.trim(),
      client: (client || '').trim(),
      vendor: (vendor || '').trim(),
      mode,
      durationMinutes,
      interviewThrough: mode === 'video' ? interviewThrough : '',
      interviewThroughOther: mode === 'video' && interviewThrough === 'Other' ? (interviewThroughOther || '').trim() : '',
      title,
    };
    setSubmitting(true);
    try {
      if (editingSlotId) {
        await updateInterviewSlot(editingSlotId, payload);
        toastManager.success('Slot rescheduled');
      } else {
        await createInterviewSlot(payload);
        toastManager.success('Slot booked successfully');
      }
      closeModal();
      loadSlots();
    } catch (err) {
      console.error('Book slot error:', err);
      toastManager.error(err.message || 'Failed to save slot');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCancelBooking = async () => {
    if (!cancelConfirmSlotId) return;
    try {
      await deleteInterviewSlot(cancelConfirmSlotId);
      toastManager.success('Booking cancelled');
      setCancelConfirmSlotId(null);
      setDetailsSlot(null);
      loadSlots();
    } catch (err) {
      console.error('Cancel slot error:', err);
      toastManager.error(err.message || 'Failed to cancel booking');
    }
  };

  const navPrev = () => {
    const d = new Date(currentDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() - 1);
    else if (viewMode === 'week') d.setDate(d.getDate() - 7);
    else d.setDate(d.getDate() - 1);
    setCurrentDate(d);
  };
  const navNext = () => {
    const d = new Date(currentDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() + 1);
    else if (viewMode === 'week') d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    setCurrentDate(d);
  };
  const goToday = () => setCurrentDate(new Date());

  const monthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const weekStart = (() => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - d.getDay());
    return d;
  })();

  const getSlotsForDay = (dayDate) => {
    const dayStart = new Date(dayDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayDate);
    dayEnd.setHours(23, 59, 59, 999);
    return slots.filter(s => {
      const start = s.startTime?.toMillis ? new Date(s.startTime.toMillis()) : s.startTime;
      const end = s.endTime?.toMillis ? new Date(s.endTime.toMillis()) : s.endTime;
      return start < dayEnd && end > dayStart;
    });
  };

  const isCurrentUserSlot = (slot) => (isAdmin ? true : (slot.bookedBy || '').toLowerCase() === (userEmail || '').toLowerCase());
  const isSlotOwnedByMe = (slot) => (slot.bookedBy || '').toLowerCase() === (userEmail || '').toLowerCase();

  const formatSlotTime = (slot) => {
    const ms = slot.startTime?.toMillis ? slot.startTime.toMillis() : (slot.startTime?.seconds ? slot.startTime.seconds * 1000 : null);
    if (!ms) return '';
    return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const renderEventContent = (slot, compact = false) => {
    if (!isCurrentUserSlot(slot)) return 'Slot not available';
    const timeStr = formatSlotTime(slot);
    const tzLabel = getTimezoneLabel(slot.timezone);
    if (compact) {
      return (
        <div className="calendar-event-details calendar-event-details--compact">
          <div className="calendar-event-details-header">{slot.name || '—'} {timeStr && <span className="calendar-event-details-time">{timeStr}</span>}</div>
          <div className="calendar-event-details-row">{[slot.vendor, slot.client].filter(Boolean).join(' · ') || '—'}</div>
          {tzLabel && <div className="calendar-event-details-row">{tzLabel}</div>}
        </div>
      );
    }
    return (
      <div className="calendar-event-details">
        <div className="calendar-event-details-header">{slot.name || '—'} {timeStr && <span className="calendar-event-details-time">{timeStr}</span>}</div>
        <div className="calendar-event-details-row">{[slot.vendor, slot.client].filter(Boolean).join(' · ') || '—'}</div>
        {tzLabel && <div className="calendar-event-details-row">{tzLabel}</div>}
      </div>
    );
  };

  const monthGrid = (() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const startPad = first.getDay();
    const days = last.getDate();
    const numRows = Math.ceil((startPad + days) / 7);
    const rows = [];
    let day = 1;
    for (let i = 0; i < numRows; i++) {
      const cells = [];
      for (let w = 0; w < 7; w++) {
        const cellIndex = i * 7 + w;
        if (cellIndex < startPad || day > days) {
          cells.push(<div key={cellIndex} className="calendar-cell calendar-cell--empty" />);
        } else {
          const cellDate = new Date(y, m, day);
          const daySlots = getSlotsForDay(cellDate);
          const isToday = (() => {
            const t = new Date();
            return cellDate.getDate() === t.getDate() && cellDate.getMonth() === t.getMonth() && cellDate.getFullYear() === t.getFullYear();
          })();
          cells.push(
            <div
              key={cellIndex}
              className={`calendar-cell ${isToday ? 'calendar-cell--today' : ''}`}
              onClick={() => openBookSlot(cellDate)}
            >
              <span className="calendar-cell-date">{day}</span>
              <div className="calendar-cell-events">
                {daySlots.slice(0, 3).map(slot => (
                  <div
                    key={slot.id}
                    className={`calendar-event ${isCurrentUserSlot(slot) ? 'calendar-event--mine' : 'calendar-event--blocked'}`}
                    onClick={isCurrentUserSlot(slot) ? (e) => { e.stopPropagation(); openSlotDetails(slot); } : undefined}
                    role={isCurrentUserSlot(slot) ? 'button' : undefined}
                  >
                    {renderEventContent(slot, true)}
                  </div>
                ))}
                {daySlots.length > 3 && <span className="calendar-cell-more">+{daySlots.length - 3} more</span>}
              </div>
            </div>
          );
          day++;
        }
      }
      rows.push(<div key={i} className="calendar-row">{cells}</div>);
    }
    return rows;
  })();

  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div className="calendar-wrap">
      <div className="section-header calendar-section-header">
        <div>
          <h1>Calendar</h1>
          <p>View and book interview slots</p>
        </div>
        {isAdmin && (
          <div className="calendar-consultant-select-wrap">
            <CustomSelect
              value={selectedConsultantEmail}
              onChange={(v) => setSelectedConsultantEmail(v || '')}
              options={[{ value: '', label: 'All consultants' }, ...consultants]}
              placeholder="Consultant"
              className="calendar-consultant-select"
            />
          </div>
        )}
      </div>
      <div className="calendar-toolbar">
        <div className="calendar-toolbar-left">
          <button type="button" className="calendar-add-btn" onClick={() => openBookSlot()} title="Add slot">
            <Plus size={20} />
          </button>
          <span className="calendar-title">{monthLabel}</span>
        </div>
        <div className="calendar-view-tabs">
          {['day', 'week', 'month', 'year'].map(v => (
            <button
              key={v}
              type="button"
              className={`calendar-view-tab ${viewMode === v ? 'active' : ''}`}
              onClick={() => setViewMode(v)}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <div className="calendar-toolbar-right">
          <button type="button" className="calendar-nav-btn" onClick={navPrev} aria-label="Previous"><ChevronLeft size={18} /></button>
          <button type="button" className="calendar-today-btn" onClick={goToday}>Today</button>
          <button type="button" className="calendar-nav-btn" onClick={navNext} aria-label="Next"><ChevronRight size={18} /></button>
        </div>
      </div>
      {viewMode === 'month' && (
        <div className="calendar-month">
          <div className="calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(w => <div key={w} className="calendar-weekday">{w}</div>)}
          </div>
          <div className="calendar-grid">
            {loading ? <div className="calendar-loading"><div className="loading-spinner" /></div> : monthGrid}
          </div>
        </div>
      )}
      {viewMode === 'week' && (
        <div className="calendar-week-view">
          <div className="calendar-week-days">
            {[0, 1, 2, 3, 4, 5, 6].map(i => {
              const d = new Date(weekStart);
              d.setDate(weekStart.getDate() + i);
              const daySlots = getSlotsForDay(d);
              return (
                <div key={i} className="calendar-week-day-col">
                  <div className="calendar-week-day-header">{d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                  <div className="calendar-week-day-cells">
                    {daySlots.map(slot => (
                      <div
                        key={slot.id}
                        className={`calendar-week-event ${isCurrentUserSlot(slot) ? 'calendar-event--mine' : 'calendar-event--blocked'}`}
                        onClick={isCurrentUserSlot(slot) ? (e) => { e.stopPropagation(); openSlotDetails(slot); } : undefined}
                        role={isCurrentUserSlot(slot) ? 'button' : undefined}
                      >
                        {renderEventContent(slot, true)}
                      </div>
                    ))}
                    <button type="button" className="calendar-week-add" onClick={() => openBookSlot(d)}>+</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {viewMode === 'day' && (
        <div className="calendar-day-view">
          <div className="calendar-day-header">{currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>
          <div className="calendar-day-slots">
            {getSlotsForDay(currentDate).map(slot => (
              <div
                key={slot.id}
                className={`calendar-day-event ${isCurrentUserSlot(slot) ? 'calendar-event--mine' : 'calendar-event--blocked'}`}
                onClick={isCurrentUserSlot(slot) ? (e) => { e.stopPropagation(); openSlotDetails(slot); } : undefined}
                role={isCurrentUserSlot(slot) ? 'button' : undefined}
              >
                {slot.startTime?.toMillis && (
                  <span className="calendar-day-event-time">
                    {new Date(slot.startTime.toMillis()).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                )}
                {renderEventContent(slot, true)}
              </div>
            ))}
            <button type="button" className="calendar-day-add" onClick={() => openBookSlot(currentDate)}>+ Add slot</button>
          </div>
        </div>
      )}
      {viewMode === 'year' && (
        <div className="calendar-year-view">
          <div className="calendar-year-grid">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(m => {
              const d = new Date(currentDate.getFullYear(), m, 1);
              return (
                <button key={m} type="button" className="calendar-year-month" onClick={() => { setCurrentDate(d); setViewMode('month'); }}>
                  {d.toLocaleDateString('en-US', { month: 'long' })}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {modalOpen && ReactDOM.createPortal(
        <div className="confirm-overlay book-slot-overlay" onClick={closeModal}>
          <div className="book-slot-modal" onClick={e => e.stopPropagation()}>
            <div className="book-slot-modal-header">
              <h2 className="book-slot-title">Book interview slot</h2>
              <button type="button" className="book-slot-close-btn" onClick={closeModal} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleBookSlotSubmit} className="book-slot-form" noValidate>
              <div className="book-slot-form-grid">
                <div className="book-slot-field">
                  <label>Name</label>
                  <input type="text" value={form.name} onChange={e => handleFormChange('name', e.target.value)} placeholder="Name" />
                  {errors.name && <span className="book-slot-field-error">{errors.name}</span>}
                </div>
                <div className="book-slot-field">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => handleFormChange('email', e.target.value)} placeholder="Email" />
                  {errors.email && <span className="book-slot-field-error">{errors.email}</span>}
                </div>
                <div className="book-slot-field">
                  <label>Vendor</label>
                  <input type="text" value={form.vendor} onChange={e => handleFormChange('vendor', e.target.value)} placeholder="Vendor" />
                  {errors.vendor && <span className="book-slot-field-error">{errors.vendor}</span>}
                </div>
                <div className="book-slot-field">
                  <label>Client</label>
                  <input type="text" value={form.client} onChange={e => handleFormChange('client', e.target.value)} placeholder="Client" />
                  {errors.client && <span className="book-slot-field-error">{errors.client}</span>}
                </div>
                <div className="book-slot-field">
                  <label>Timezone</label>
                  <CustomSelect value={form.timezone} onChange={(v) => handleFormChange('timezone', v)} options={TIMEZONE_OPTIONS} />
                  {errors.timezone && <span className="book-slot-field-error">{errors.timezone}</span>}
                </div>
                <div className="book-slot-field">
                  <label>Date</label>
                  <input type="date" value={form.date} min={todayStr} onChange={e => handleFormChange('date', e.target.value)} />
                  {errors.date && <span className="book-slot-field-error">{errors.date}</span>}
                </div>
                <div className="book-slot-field">
                  <label>Mode of interview</label>
                  <CustomSelect value={form.mode} onChange={(v) => handleFormChange('mode', v)} options={MODE_OPTIONS} />
                  {errors.mode && <span className="book-slot-field-error">{errors.mode}</span>}
                </div>
                <div className="book-slot-field">
                  <label>Time (HH:MM AM/PM)</label>
                  <div className="time-input-row">
                    <CustomSelect
                      value={time24To12(form.time || '09:00').hour}
                      onChange={(v) => handleFormChange('time', time12To24(v, time24To12(form.time || '09:00').minute, time24To12(form.time || '09:00').amPm))}
                      options={HOUR_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
                      placeholder="Hour"
                      className="time-input-hour"
                    />
                    <span className="time-input-sep">:</span>
                    <CustomSelect
                      value={time24To12(form.time || '09:00').minute}
                      onChange={(v) => handleFormChange('time', time12To24(time24To12(form.time || '09:00').hour, v, time24To12(form.time || '09:00').amPm))}
                      options={MINUTE_OPTIONS.map((n) => ({ value: n, label: String(n).padStart(2, '0') }))}
                      placeholder="Min"
                      className="time-input-minute"
                    />
                    <div className="time-input-ampm">
                      <button type="button" className={`time-ampm-btn ${(time24To12(form.time || '09:00').amPm === 'AM' ? 'active' : '')}`} onClick={() => handleFormChange('time', time12To24(time24To12(form.time || '09:00').hour, time24To12(form.time || '09:00').minute, 'AM'))}>AM</button>
                      <button type="button" className={`time-ampm-btn ${(time24To12(form.time || '09:00').amPm === 'PM' ? 'active' : '')}`} onClick={() => handleFormChange('time', time12To24(time24To12(form.time || '09:00').hour, time24To12(form.time || '09:00').minute, 'PM'))}>PM</button>
                    </div>
                  </div>
                  {errors.time && <span className="book-slot-field-error">{errors.time}</span>}
                </div>
                <div className="book-slot-field">
                  <label>Duration (minutes)</label>
                  <CustomSelect value={form.durationMinutes} onChange={(v) => handleFormChange('durationMinutes', v)} options={DURATION_OPTIONS.map((n) => ({ value: n, label: `${n} min` }))} />
                  {errors.durationMinutes && <span className="book-slot-field-error">{errors.durationMinutes}</span>}
                </div>
                {form.mode === 'video' && (
                  <>
                    <div className="book-slot-field">
                      <label>Interview through</label>
                      <CustomSelect value={form.interviewThrough} onChange={(v) => handleFormChange('interviewThrough', v)} options={INTERVIEW_THROUGH_OPTIONS.map((opt) => ({ value: opt, label: opt }))} />
                      {errors.interviewThrough && <span className="book-slot-field-error">{errors.interviewThrough}</span>}
                    </div>
                    {form.interviewThrough === 'Other' && (
                      <div className="book-slot-field book-slot-field--full">
                        <label>Other (specify)</label>
                        <input type="text" value={form.interviewThroughOther} onChange={e => handleFormChange('interviewThroughOther', e.target.value)} placeholder="e.g. Webex" />
                        {errors.interviewThroughOther && <span className="book-slot-field-error">{errors.interviewThroughOther}</span>}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="book-slot-actions">
                <button type="button" className="confirm-btn cancel-btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="confirm-btn confirm-primary" disabled={submitting}>{submitting ? (editingSlotId ? 'Updating…' : 'Booking…') : (editingSlotId ? 'Update slot' : 'Book slot')}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {detailsSlot && ReactDOM.createPortal(
        <div className="confirm-overlay book-slot-overlay" onClick={closeSlotDetails}>
          <div className="slot-details-modal" onClick={e => e.stopPropagation()}>
            <div className="book-slot-modal-header slot-details-modal-header">
              <h2 className="book-slot-title">Booking details</h2>
              <button type="button" className="book-slot-close-btn" onClick={closeSlotDetails} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <div className="slot-details-body">
              <div className="slot-details-success">
                <CheckCircle2 size={48} className="slot-details-success-icon" />
                <p className="slot-details-success-text">Slot has been booked successfully</p>
              </div>
              <dl className="slot-details-list">
                <div className="slot-details-row"><dt>Name</dt><dd>{detailsSlot.name || '—'}</dd></div>
                <div className="slot-details-row"><dt>Email</dt><dd>{detailsSlot.email || '—'}</dd></div>
                <div className="slot-details-row"><dt>Date</dt><dd>{detailsSlot.startTime?.toMillis ? new Date(detailsSlot.startTime.toMillis()).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</dd></div>
                <div className="slot-details-row"><dt>Time</dt><dd>{formatSlotTime(detailsSlot) || '—'}</dd></div>
                <div className="slot-details-row"><dt>Duration</dt><dd>{detailsSlot.durationMinutes ? `${detailsSlot.durationMinutes} min` : '—'}</dd></div>
                <div className="slot-details-row"><dt>Vendor</dt><dd>{detailsSlot.vendor || '—'}</dd></div>
                <div className="slot-details-row"><dt>Client</dt><dd>{detailsSlot.client || '—'}</dd></div>
                <div className="slot-details-row"><dt>Timezone</dt><dd>{getTimezoneLabel(detailsSlot.timezone) || detailsSlot.timezone || '—'}</dd></div>
                <div className="slot-details-row"><dt>Mode</dt><dd>{detailsSlot.mode === 'video' ? 'Video' : detailsSlot.mode === 'audio' ? 'Audio' : detailsSlot.mode || '—'}</dd></div>
                {detailsSlot.mode === 'video' && (
                  <>
                    <div className="slot-details-row"><dt>Interview through</dt><dd>{detailsSlot.interviewThrough || '—'}</dd></div>
                    {detailsSlot.interviewThrough === 'Other' && detailsSlot.interviewThroughOther && (
                      <div className="slot-details-row"><dt>Other</dt><dd>{detailsSlot.interviewThroughOther}</dd></div>
                    )}
                  </>
                )}
              </dl>
            </div>
            {isSlotOwnedByMe(detailsSlot) && (
              <div className="slot-details-footer">
                <button type="button" className="confirm-btn cancel-btn" onClick={() => setCancelConfirmSlotId(detailsSlot.id)}>Cancel booking</button>
                <button type="button" className="confirm-btn confirm-primary" onClick={() => openReschedule(detailsSlot)}>Reschedule</button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {cancelConfirmSlotId && ReactDOM.createPortal(
        <div className="confirm-overlay" onClick={() => setCancelConfirmSlotId(null)}>
          <div className="slot-cancel-confirm-dialog" onClick={e => e.stopPropagation()}>
            <p className="slot-cancel-confirm-text">Are you sure you want to cancel this booking?</p>
            <div className="slot-cancel-confirm-actions">
              <button type="button" className="confirm-btn cancel-btn" onClick={() => setCancelConfirmSlotId(null)}>No</button>
              <button type="button" className="confirm-btn confirm-primary" onClick={handleConfirmCancelBooking}>Yes</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

const HOURS_OPTIONS_TS = [];
for (let h = 0; h <= 48; h++) HOURS_OPTIONS_TS.push(h * 0.5);
const DAY_LABELS_TS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function TimesheetSection({ userEmail, isAdmin }) {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [worklogs, setWorklogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [selection, setSelection] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [form, setForm] = useState({
    projectName: '', workingFor: '', startTime: '09:00', endTime: '17:00', hours: 8, comment: '',
  });
  const [saving, setSaving] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [formErrors, setFormErrors] = useState({ projectName: '', workingFor: '' });
  const [viewWorklog, setViewWorklog] = useState(null);
  const [viewMode, setViewMode] = useState('week');
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [hoveredWorklog, setHoveredWorklog] = useState(null);
  const [hoveredCellRect, setHoveredCellRect] = useState(null);

  const effectiveUser = isAdmin && selectedUserEmail ? selectedUserEmail : userEmail;
  const supportDisplayName = isAdmin && effectiveUser
    ? (users.find((u) => u.email === effectiveUser)?.displayName || effectiveUser.split('@')[0])
    : '';
  const { start: weekStartDate, end: weekEndDate } = getWeekRange(weekStart);
  const { start: monthStartDate, end: monthEndDate } = getMonthRange(monthStart);
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStartDate);
    d.setDate(weekStartDate.getDate() + i);
    weekDays.push(d);
  }
  const monthDays = [];
  for (let i = 1; i <= new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate(); i++) {
    const d = new Date(monthStart.getFullYear(), monthStart.getMonth(), i);
    monthDays.push(d);
  }
  const displayDays = viewMode === 'week' ? weekDays : monthDays;

  const monthGridData = React.useMemo(() => {
    const y = monthStart.getFullYear();
    const m = monthStart.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const startPad = first.getDay();
    const days = last.getDate();
    const numRows = Math.ceil((startPad + days) / 7);
    const rows = [];
    for (let row = 0; row < numRows; row++) {
      const cells = [];
      for (let col = 0; col < 7; col++) {
        const cellIndex = row * 7 + col;
        if (cellIndex < startPad) {
          cells.push({ empty: true });
        } else {
          const day = cellIndex - startPad + 1;
          if (day > days) {
            cells.push({ empty: true });
          } else {
            const cellDate = new Date(y, m, day);
            const dayIndex = day - 1;
            const today = new Date();
            const isToday = cellDate.getDate() === today.getDate() && cellDate.getMonth() === today.getMonth() && cellDate.getFullYear() === today.getFullYear();
            cells.push({ empty: false, cellDate, dayIndex, isToday });
          }
        }
      }
      rows.push(cells);
    }
    return rows;
  }, [monthStart]);

  const loadWorklogs = React.useCallback(async () => {
    if (!effectiveUser) return;
    setLoading(true);
    try {
      if (viewMode === 'week') {
        const list = await getWorklogsForWeek(effectiveUser, weekStart);
        setWorklogs(list);
      } else {
        const { start, end } = getMonthRange(monthStart);
        const list = await getWorklogsForRange(effectiveUser, start, end);
        setWorklogs(list);
      }
    } catch (err) {
      console.error('Load worklogs error:', err);
      toastManager.error('Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  }, [effectiveUser, weekStart, monthStart, viewMode]);

  useEffect(() => { loadWorklogs(); }, [loadWorklogs]);

  useEffect(() => {
    if (isAdmin) {
      getSupportUsers().then((emails) => {
        const list = (emails || []).map((email) => ({ email, displayName: email.split('@')[0] }));
        setUsers(list);
        if (list.length > 0 && !selectedUserEmail) setSelectedUserEmail(list[0].email);
        if (list.length === 0) setSelectedUserEmail('');
      });
    }
  }, [isAdmin]);

  const goPrev = () => {
    if (viewMode === 'week') {
      setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; });
    } else {
      setMonthStart((d) => { const n = new Date(d); n.setMonth(n.getMonth() - 1); return n; });
    }
    setSelection(null); setPopupOpen(false); setViewWorklog(null);
  };
  const goNext = () => {
    if (viewMode === 'week') {
      setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; });
    } else {
      setMonthStart((d) => { const n = new Date(d); n.setMonth(n.getMonth() + 1); return n; });
    }
    setSelection(null); setPopupOpen(false); setViewWorklog(null);
  };
  const goToday = () => {
    const now = new Date();
    const weekD = new Date(now);
    weekD.setDate(weekD.getDate() - weekD.getDay());
    weekD.setHours(0, 0, 0, 0);
    setWeekStart(weekD);
    const monthD = new Date(now.getFullYear(), now.getMonth(), 1);
    monthD.setHours(0, 0, 0, 0);
    setMonthStart(monthD);
    setSelection(null);
    setPopupOpen(false);
    setViewWorklog(null);
  };
  const formatPeriodLabel = () =>
    viewMode === 'week'
      ? `${weekStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${weekEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      : monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const periodTotal = worklogs.reduce((sum, w) => sum + (Number(w.hours) || 0), 0);

  const isCellInSelection = (dayIndex, hour) => {
    if (!selection) return false;
    const { dayStart, dayEnd, hourStart, hourEnd } = selection;
    if (dayIndex < dayStart || dayIndex > dayEnd) return false;
    if (dayIndex === dayStart && dayIndex === dayEnd) return hour >= hourStart && hour < hourEnd;
    if (dayIndex === dayStart) return hour >= hourStart;
    if (dayIndex === dayEnd) return hour < hourEnd;
    return true;
  };

  const getWorklogForCell = (dayIndex, hour) => {
    const dateStr = toDateString(displayDays[dayIndex]);
    return worklogs.find((w) => {
      if (w.date !== dateStr) return false;
      const [sh] = (w.startTime || '').split(':').map(Number);
      const [eh] = (w.endTime || '').split(':').map(Number);
      return hour >= sh && hour < eh;
    });
  };

  const getTotalHoursForDay = (dayIndex) => {
    const dateStr = toDateString(displayDays[dayIndex]);
    const total = worklogs
      .filter((w) => w.date === dateStr)
      .reduce((sum, w) => sum + (Number(w.hours) || 0), 0);
    return total;
  };

  const getWorklogsForDay = (dayIndex) => {
    const dateStr = toDateString(monthDays[dayIndex]);
    return worklogs
      .filter((w) => w.date === dateStr)
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  };

  const handleCellMouseDown = (dayIndex, hour) => {
    setDragStart({ dayIndex, hour });
    setSelection({ dayStart: dayIndex, dayEnd: dayIndex, hourStart: hour, hourEnd: hour + 1 });
  };

  const handleCellMouseEnter = (dayIndex, hour) => {
    if (dragStart == null) return;
    const dayStart = Math.min(dragStart.dayIndex, dayIndex);
    const dayEnd = Math.max(dragStart.dayIndex, dayIndex);
    const hourStart = dayStart === dragStart.dayIndex ? dragStart.hour : hour;
    const hourEnd = dayEnd === dragStart.dayIndex ? hour + 1 : hour;
    setSelection({
      dayStart, dayEnd,
      hourStart: Math.min(hourStart, hour),
      hourEnd: Math.max(hourEnd, hour + 1),
    });
  };

  const handleCellMouseUp = () => {
    if (dragStart != null && selection) {
      const { dayStart, dayEnd, hourStart, hourEnd } = selection;
      if (dayStart === dayEnd && hourEnd - hourStart >= 1) {
        const cellWorklog = getWorklogForCell(dayStart, hourStart);
        if (cellWorklog) {
          setViewWorklog(cellWorklog);
        } else {
          setForm((f) => ({ ...f, startTime: `${String(hourStart).padStart(2, '0')}:00`, endTime: `${String(hourEnd).padStart(2, '0')}:00`, hours: hourEnd - hourStart }));
          setFormErrors({ projectName: '', workingFor: '' });
          setPopupOpen(true);
        }
      }
    }
    setDragStart(null);
  };

  useEffect(() => {
    const onMouseUp = () => handleCellMouseUp();
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, [selection, dragStart]);

  const closePopup = () => {
    setPopupOpen(false);
    setSelection(null);
    setFormErrors({ projectName: '', workingFor: '' });
  };

  const closeViewPopup = () => {
    setViewWorklog(null);
    setSelection(null);
  };

  const validateForm = () => {
    const err = { projectName: '', workingFor: '' };
    if (!form.projectName?.trim()) err.projectName = 'Project name is required';
    if (!form.workingFor?.trim()) err.workingFor = 'Working for is required';
    setFormErrors(err);
    return !err.projectName && !err.workingFor;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!effectiveUser || !selection) return;
    if (!validateForm()) return;
    setSaving(true);
    try {
      await saveWorklog({
        userEmail: effectiveUser,
        date: toDateString(displayDays[selection.dayStart]),
        startTime: form.startTime,
        endTime: form.endTime,
        hours: form.hours,
        projectName: form.projectName.trim(),
        workingFor: form.workingFor.trim(),
        comment: form.comment.trim(),
      });
      toastManager.success('Time entry saved');
      closePopup();
      loadWorklogs();
    } catch (err) {
      console.error('Save worklog error:', err);
      toastManager.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="timesheet-section">
      <div className="timesheet-header-row">
        <h1 className="timesheet-title">Timesheets</h1>
        <div className="timesheet-header-right">
          {isAdmin && (
            <div className="timesheet-user-select timesheet-user-select-top">
              <label htmlFor="timesheet-user">User</label>
              <select id="timesheet-user" value={selectedUserEmail} onChange={(e) => setSelectedUserEmail(e.target.value)} className="timesheet-select">
                {users.map((u) => <option key={u.email} value={u.email}>{u.displayName || u.email}</option>)}
              </select>
              {users.length === 0 && <span className="timesheet-no-users-hint">No support users yet</span>}
            </div>
          )}
        </div>
      </div>
      {isAdmin && <p className="timesheet-subtitle">View and manage timesheets</p>}
      <div className="timesheet-toolbar">
        <button type="button" className="timesheet-btn today-btn" onClick={goToday}>Today</button>
        <div className="timesheet-view-mode-dropdown">
          <CustomSelect
            value={viewMode}
            onChange={(v) => setViewMode(v)}
            options={[{ value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }]}
            className="timesheet-view-select-wrap"
          />
        </div>
        <div className="timesheet-nav">
          <button type="button" className="timesheet-btn nav-btn" onClick={goPrev} aria-label="Previous"><ChevronLeft size={18} /></button>
          <span className="timesheet-period-label">{formatPeriodLabel()}</span>
          <button type="button" className="timesheet-btn nav-btn" onClick={goNext} aria-label="Next"><ChevronRight size={18} /></button>
        </div>
        {effectiveUser && (
          <div className="timesheet-period-total timesheet-period-total-toolbar">
            Total: {periodTotal % 1 === 0 ? periodTotal : periodTotal.toFixed(1)} Hrs
          </div>
        )}
      </div>
      {loading ? (
        <div className="timesheet-loading"><div className="loading-spinner" /><p>Loading timesheet...</p></div>
      ) : !effectiveUser ? (
        <div className="timesheet-empty"><p>{isAdmin ? 'Select a user to view timesheet.' : 'You are not signed in.'}</p></div>
      ) : viewMode === 'week' ? (
        <div className="timesheet-grid-wrapper">
          <table className="timesheet-grid" role="grid">
            <thead>
              <tr>
                <th className="timesheet-corner">Time</th>
                {displayDays.map((d, i) => {
                  const totalHrs = getTotalHoursForDay(i);
                  return (
                    <th key={i} className="timesheet-day-header">
                      <span className="day-name">{DAY_LABELS_TS[d.getDay()]}</span>
                      <div className="timesheet-day-header-row">
                        <span className="day-date">{`${d.getMonth() + 1}/${d.getDate()}`}</span>
                        <span className="timesheet-day-header-hours">{totalHrs ? `${totalHrs % 1 === 0 ? totalHrs : totalHrs.toFixed(1)}Hrs` : '0Hrs'}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 24 }, (_, hour) => (
                <tr key={hour}>
                  <td className="timesheet-time-col">
                    {hour === 0 ? '12 AM' : hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`}
                  </td>
                  {displayDays.map((_, dayIndex) => {
                    const worklog = getWorklogForCell(dayIndex, hour);
                    const selected = isCellInSelection(dayIndex, hour);
                    return (
                      <td
                        key={`${dayIndex}-${hour}`}
                        className={`timesheet-cell ${worklog ? 'filled' : ''} ${selected ? 'selected' : ''}`}
                        onMouseDown={() => handleCellMouseDown(dayIndex, hour)}
                        onMouseEnter={(e) => {
                          handleCellMouseEnter(dayIndex, hour);
                          if (worklog) {
                            setHoveredWorklog(worklog);
                            setHoveredCellRect(e.currentTarget.getBoundingClientRect());
                          }
                        }}
                        onMouseLeave={() => {
                          setHoveredWorklog(null);
                          setHoveredCellRect(null);
                        }}
                        role="gridcell"
                      >
                        {worklog && (
                          <div className="timesheet-cell-entry">
                            {isAdmin ? (
                              <>
                                <div className="cell-line1">
                                  <span className="cell-primary cell-project">{worklog.projectName || '—'}</span>
                                  <span className="cell-hours">{worklog.hours} h</span>
                                </div>
                                <div className="cell-line2 cell-working-for">{worklog.workingFor || '—'}</div>
                              </>
                            ) : (
                              <>
                                <div className="cell-line1">
                                  <span className="cell-primary cell-project">{worklog.projectName || '—'}</span>
                                  <span className="cell-hours">{worklog.hours} h</span>
                                </div>
                                <div className="cell-line2 cell-working-for">{worklog.workingFor || '—'}</div>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="timesheet-grid-wrapper timesheet-grid-wrapper-month">
          <table className="timesheet-grid timesheet-grid-month" role="grid">
            <thead>
              <tr className="timesheet-month-weekdays">
                {DAY_LABELS_TS.map((label, i) => (
                  <th key={i} className="timesheet-month-weekday">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthGridData.map((rowCells, rowIdx) => (
                <tr key={rowIdx} className="timesheet-month-week-row">
                  {rowCells.map((cell, colIdx) => (
                    cell.empty ? (
                      <td key={colIdx} className="timesheet-month-cell timesheet-month-cell-empty" />
                    ) : (
                      <td key={colIdx} className={`timesheet-month-cell timesheet-month-day-cell ${cell.isToday ? 'timesheet-month-cell--today' : ''}`}>
                        <div className="timesheet-month-day-header">
                          <span className="timesheet-month-day-date">
                            {cell.cellDate.getDate() === 1
                              ? `1 ${cell.cellDate.toLocaleDateString('en-US', { month: 'short' })}`
                              : cell.cellDate.getDate()}
                          </span>
                          <span className="timesheet-month-day-total">
                            {(() => {
                              const h = getTotalHoursForDay(cell.dayIndex);
                              return h ? `${h % 1 === 0 ? h : h.toFixed(1)}Hrs` : '0Hrs';
                            })()}
                          </span>
                        </div>
                        <div className="timesheet-month-day-worklogs">
                          {getWorklogsForDay(cell.dayIndex).map((w, idx) => (
                            <div
                              key={w.id || idx}
                              className="timesheet-month-worklog-line"
                              onMouseEnter={(e) => {
                                setHoveredWorklog(w);
                                setHoveredCellRect(e.currentTarget.getBoundingClientRect());
                              }}
                              onMouseLeave={() => {
                                setHoveredWorklog(null);
                                setHoveredCellRect(null);
                              }}
                            >
                              {w.projectName || '—'} · {w.hours} h
                            </div>
                          ))}
                        </div>
                      </td>
                    )
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {popupOpen && selection && ReactDOM.createPortal(
        <div className="timesheet-popup-overlay" onClick={closePopup} role="dialog" aria-modal="true">
          <div className="timesheet-popup" onClick={(e) => e.stopPropagation()}>
            <div className="timesheet-popup-header">
              <h3>Log time</h3>
              <button type="button" className="timesheet-popup-close" onClick={closePopup} aria-label="Close"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="timesheet-popup-form">
              <div className="timesheet-form-row">
                <div className="timesheet-form-group">
                  <label htmlFor="ts-project">Project name <span className="timesheet-required">*</span></label>
                  <input id="ts-project" type="text" value={form.projectName} onChange={(e) => { setForm((f) => ({ ...f, projectName: e.target.value })); setFormErrors((e2) => ({ ...e2, projectName: '' })); }} placeholder="Project name" className={`timesheet-input ${formErrors.projectName ? 'timesheet-input-error' : ''}`} />
                  {formErrors.projectName && <span className="timesheet-field-error">{formErrors.projectName}</span>}
                </div>
                <div className="timesheet-form-group">
                  <label htmlFor="ts-working-for">Working for <span className="timesheet-required">*</span></label>
                  <input id="ts-working-for" type="text" value={form.workingFor} onChange={(e) => { setForm((f) => ({ ...f, workingFor: e.target.value })); setFormErrors((e2) => ({ ...e2, workingFor: '' })); }} placeholder="Working for" className={`timesheet-input ${formErrors.workingFor ? 'timesheet-input-error' : ''}`} />
                  {formErrors.workingFor && <span className="timesheet-field-error">{formErrors.workingFor}</span>}
                </div>
              </div>
              <div className="timesheet-form-row">
                <div className="timesheet-form-group">
                  <label htmlFor="ts-start">Start time</label>
                  <input id="ts-start" type="time" value={form.startTime} onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))} className="timesheet-input" />
                </div>
                <div className="timesheet-form-group">
                  <label htmlFor="ts-end">End time</label>
                  <input id="ts-end" type="time" value={form.endTime} onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))} className="timesheet-input" />
                </div>
              </div>
              <div className="timesheet-form-row">
                <div className="timesheet-form-group">
                  <label htmlFor="ts-hours">Hours</label>
                  <select id="ts-hours" value={form.hours} onChange={(e) => setForm((f) => ({ ...f, hours: parseFloat(e.target.value) }))} className="timesheet-select timesheet-hours-select">
                    {HOURS_OPTIONS_TS.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>
              <div className="timesheet-form-group">
                <label htmlFor="ts-comment">Comment</label>
                <textarea id="ts-comment" value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} placeholder="Comment (optional)" rows={3} className="timesheet-textarea" />
              </div>
              <div className="timesheet-popup-footer">
                <button type="button" className="timesheet-btn cancel-btn" onClick={closePopup}>Cancel</button>
                <button type="submit" className="timesheet-btn save-btn" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
      {viewWorklog && ReactDOM.createPortal(
        <div className="timesheet-popup-overlay" onClick={closeViewPopup} role="dialog" aria-modal="true" aria-label="View time entry">
          <div className="timesheet-popup timesheet-view-only" onClick={(e) => e.stopPropagation()}>
            <div className="timesheet-popup-header">
              <h3>View time entry</h3>
              <button type="button" className="timesheet-popup-close" onClick={closeViewPopup} aria-label="Close"><X size={20} /></button>
            </div>
            {isAdmin && (
              <div className="timesheet-view-user">
                <span className="timesheet-view-user-label">Support:</span>
                <span className="timesheet-view-user-value">{supportDisplayName || viewWorklog.userEmail}</span>
              </div>
            )}
            <div className="timesheet-popup-form">
              <div className="timesheet-form-row">
                <div className="timesheet-form-group">
                  <label>Project name</label>
                  <div className="timesheet-input timesheet-input-readonly">{viewWorklog.projectName || '—'}</div>
                </div>
                <div className="timesheet-form-group">
                  <label>Working for</label>
                  <div className="timesheet-input timesheet-input-readonly">{viewWorklog.workingFor || '—'}</div>
                </div>
              </div>
              <div className="timesheet-form-row">
                <div className="timesheet-form-group">
                  <label>Start time</label>
                  <div className="timesheet-input timesheet-input-readonly">{viewWorklog.startTime || '—'}</div>
                </div>
                <div className="timesheet-form-group">
                  <label>End time</label>
                  <div className="timesheet-input timesheet-input-readonly">{viewWorklog.endTime || '—'}</div>
                </div>
              </div>
              <div className="timesheet-form-row">
                <div className="timesheet-form-group">
                  <label>Hours</label>
                  <div className="timesheet-input timesheet-input-readonly">{viewWorklog.hours != null ? viewWorklog.hours : '—'}</div>
                </div>
              </div>
              <div className="timesheet-form-group">
                <label>Comment</label>
                <div className="timesheet-input timesheet-input-readonly timesheet-comment-readonly">{viewWorklog.comment || '—'}</div>
              </div>
              <div className="timesheet-popup-footer">
                <button type="button" className="timesheet-btn save-btn" onClick={closeViewPopup}>Close</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {hoveredWorklog && hoveredCellRect && ReactDOM.createPortal(
        <div
          className="timesheet-cell-tooltip"
          style={{
            position: 'fixed',
            left: hoveredCellRect.left + hoveredCellRect.width / 2 - 100,
            top: hoveredCellRect.bottom + 6,
            pointerEvents: 'none',
          }}
          role="tooltip"
        >
          <div className="timesheet-cell-tooltip-inner">
            <div className="timesheet-cell-tooltip-row"><strong>Project:</strong> {hoveredWorklog.projectName || '—'}</div>
            <div className="timesheet-cell-tooltip-row"><strong>Worked for:</strong> {hoveredWorklog.workingFor || '—'}</div>
            <div className="timesheet-cell-tooltip-row"><strong>Time:</strong> {hoveredWorklog.startTime || ''} – {hoveredWorklog.endTime || ''} ({hoveredWorklog.hours} h)</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function useTween(target, duration = 900) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (target == null || Number.isNaN(Number(target))) {
      setValue(0);
      return undefined;
    }
    const to = Number(target);
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setValue(to);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setValue(to * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
}

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { modules, loading: roleLoading, role, user } = useUserRole();
  const activeSection = PATH_TO_SECTION[location.pathname] || 'dashboard';
  const [uploadHistory, setUploadHistory] = useState([]);
  const [quizSessions, setQuizSessions] = useState([]);
  const [adminSessions, setAdminSessions] = useState([]);
  const isDemoMode = (() => {
    try {
      const param = new URLSearchParams(window.location.search).get('demo');
      if (param === '1') window.sessionStorage.setItem('demoMode', '1');
      else if (param === '0' || param === 'off') window.sessionStorage.removeItem('demoMode');
      return window.sessionStorage.getItem('demoMode') === '1';
    } catch { return false; }
  })();
  const DEMO_READY_QUIZZES = [
    { id: 'demo-p1', title: 'ICD-10 Coding', quizFileUrl: '' },
    { id: 'demo-p2', title: 'Medical Procedures', quizFileUrl: '' },
    { id: 'demo-p3', title: 'Compliance Basics', quizFileUrl: '' },
  ];
  const [quizFiles, setQuizFiles] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [uploadRefreshTrigger, setUploadRefreshTrigger] = useState(0);
  const [completionStats, setCompletionStats] = useState(null);
  const [completionsByUser, setCompletionsByUser] = useState([]);
  const [deleteSessionConfirm, setDeleteSessionConfirm] = useState(null);
  const [deleteQuizFileConfirm, setDeleteQuizFileConfirm] = useState(null);
  const [metricsView, setMetricsView] = useState('overview'); // overview | pulse

  useEffect(() => {
    if (roleLoading) return;
    const firstAllowedPath = modules.length > 0 ? SECTION_TO_PATH[modules[0]] : SECTION_TO_PATH.quiz;
    if (role === 'consultant' && (activeSection === 'dashboard' || activeSection === 'calendar')) {
      navigate(firstAllowedPath, { replace: true });
      return;
    }
    if (activeSection === 'users' && !modules.includes('users')) {
      navigate(firstAllowedPath);
    } else if (activeSection === 'reports' && !modules.includes('reports')) {
      navigate(firstAllowedPath);
    } else if (activeSection === 'dashboard' && !modules.includes('dashboard')) {
      navigate(firstAllowedPath);
    } else if (activeSection === 'quiz' && !modules.includes('quiz')) {
      navigate(firstAllowedPath);
    } else if (activeSection === 'calendar' && !modules.includes('calendar')) {
      navigate(firstAllowedPath);
    } else if (activeSection === 'timesheets' && !modules.includes('timesheets')) {
      navigate(firstAllowedPath);
    }
  }, [activeSection, modules, navigate, roleLoading, role]);

  const loadQuizData = React.useCallback(async () => {
    if (!user?.email) return;
    setQuizLoading(true);
    try {
      const isAdmin = role === 'admin';
      const [sessions, files, adminList] = await Promise.all([
        getSessionsForUser(user.email),
        getQuizFiles(),
        isAdmin ? getSessionsForAdmin(user.email) : Promise.resolve([]),
      ]);
      setQuizSessions(sessions);
      setQuizFiles(files);
      setAdminSessions(adminList);
    } catch (err) {
      console.error('Failed to load quiz data:', err);
    } finally {
      setQuizLoading(false);
    }
  }, [user?.email, role]);

  useEffect(() => {
    if (activeSection !== 'quiz' || !user?.email) return;
    loadQuizData();
  }, [activeSection, user?.email, loadQuizData]);

  const loadCompletionStats = React.useCallback(async () => {
    try {
      const stats = await getCompletionStats(user?.email || '', role === 'admin');
      setCompletionStats({
        totalCompletions: stats?.totalCompletions || 0,
        myCompletions: stats?.myCompletions || 0,
        avgScore: stats?.avgScore || 0,
        uniqueUsers: stats?.uniqueUsers || 0,
      });
      setCompletionsByUser(Array.isArray(stats?.byUser) ? stats.byUser : []);
    } catch (err) {
      console.error('Failed to load completion stats:', err);
      setCompletionStats({ totalCompletions: 0, myCompletions: 0, avgScore: 0, uniqueUsers: 0 });
      setCompletionsByUser([]);
    }
  }, [user?.email, role]);

  useEffect(() => {
    if (activeSection === 'dashboard') {
      loadCompletionStats();
    }
  }, [activeSection, loadCompletionStats]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const handleUploadSuccess = (result) => {
    const uploadInfo = {
      id: Date.now(),
      fileName: result.path.split('/').pop(),
      path: result.path,
      downloadUrl: result.downloadUrl,
      timestamp: new Date().toLocaleString()
    };
    
    setUploadHistory(prev => [uploadInfo, ...prev]);
    toastManager.success('Quiz uploaded successfully!');
  };

  const handleStartQuiz = (session) => {
    window.location.href = buildQuizPlayerUrl({
      quizFileUrl: session.quizFileUrl,
      sessionId: session.id,
      durationMinutes: session.durationMinutes,
    });
  };

  const handleDeleteSession = async (sessionId) => {
    if (!sessionId) return;
    setDeleteSessionConfirm(null);
    try {
      await deleteSession(sessionId);
      toastManager.success('Session deleted');
      loadQuizData();
    } catch (err) {
      console.error('Failed to delete session:', err);
      toastManager.error(err.message || 'Failed to delete session');
    }
  };

  const handleDeleteQuizFile = async (fileId) => {
    if (!fileId) return;
    setDeleteQuizFileConfirm(null);
    try {
      await deleteQuizFile(fileId);
      toastManager.success('Quiz file removed from history');
      loadQuizData();
    } catch (err) {
      console.error('Failed to delete quiz file:', err);
      toastManager.error(err.message || 'Failed to delete quiz file');
    }
  };

  const isAdmin = role === 'admin';
  const displayAvgScore = completionStats?.avgScore ?? null;
  const displayCompletions = completionStats
    ? (isAdmin ? completionStats.totalCompletions : completionStats.myCompletions)
    : null;
  const displayUniqueUsers = isAdmin ? (completionStats?.uniqueUsers ?? null) : null;
  const metricsActive = activeSection === 'dashboard';
  const tweenAvg = useTween(metricsActive ? displayAvgScore : null, 1100);
  const tweenCompletions = useTween(metricsActive ? displayCompletions : null, 950);
  const tweenUsers = useTween(metricsActive ? displayUniqueUsers : null, 950);

  const renderSection = (overrideSection) => {
    const section = overrideSection ?? activeSection;
    switch (section) {
      case 'dashboard':
        if (role === 'consultant' || !modules.includes('dashboard')) {
          return null;
        }
        if (!isAdmin) {
          return (
            <>
              <div className="section-header">
                <h1>Training Portal</h1>
                <p>Your quiz progress, modules, and achievements</p>
              </div>
              <TrainingDashboard user={user} />
            </>
          );
        }
        return (
          <>
            <div className="section-header">
              <h1>Quiz Dashboard</h1>
              <p>Overview of your quiz platform</p>
            </div>

            <div className="dashboard-card dashboard-progress-card dash-field-in">
              <div
                className="progress-ring"
                style={{ '--ring-value': displayAvgScore != null ? tweenAvg : 0 }}
              >
                <div className="progress-ring-inner">
                  <div className="progress-ring-value">
                    {displayAvgScore != null ? `${Math.round(tweenAvg)}%` : '—'}
                  </div>
                  <div className="progress-ring-label">Avg Score</div>
                </div>
              </div>
              <div className="dashboard-progress-copy">
                <h3>Overall Progress</h3>
                <p>
                  {isAdmin
                    ? 'Average score across all consultant quiz completions.'
                    : 'Your average score across completed quizzes.'}
                </p>
              </div>
            </div>

            <div className="dashboard-grid">
              <div className="dashboard-card stat-card dash-field-in dash-delay-1">
                <div className="card-header">
                  <h3>Total Completions</h3>
                  <CheckCircle2 className="card-icon" size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-number">
                    {displayCompletions != null ? Math.round(tweenCompletions) : '—'}
                  </div>
                  <div className="stat-description">
                    {isAdmin ? 'All quiz completions (platform-wide)' : 'My completed quizzes'}
                  </div>
                </div>
              </div>

              <div className="dashboard-card stat-card dash-field-in dash-delay-2">
                <div className="card-header">
                  <h3>Average Score</h3>
                  <BarChart3 className="card-icon" size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-number">
                    {displayAvgScore != null ? `${Math.round(tweenAvg)}%` : '—'}
                  </div>
                  <div className="stat-description">
                    {isAdmin ? 'Across all users' : 'My average score'}
                  </div>
                </div>
              </div>

              {isAdmin && (
                <div className="dashboard-card stat-card dash-field-in dash-delay-3">
                  <div className="card-header">
                    <h3>Unique Users</h3>
                    <Users className="card-icon" size={24} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {displayUniqueUsers != null ? Math.round(tweenUsers) : '—'}
                    </div>
                    <div className="stat-description">
                      Users who completed at least one quiz
                    </div>
                  </div>
                </div>
              )}
            </div>

            {isAdmin && completionsByUser.length > 0 && (() => {
              const chartData = [...completionsByUser]
                .map((u) => ({
                  ...u,
                  name: (u.userEmail || '').split('@')[0],
                  fullEmail: u.userEmail,
                  initial: ((u.userEmail || '?')[0] || '?').toUpperCase(),
                }))
                .sort((a, b) => (b.count || 0) - (a.count || 0));
              const totalComp = chartData.reduce((s, u) => s + (u.count || 0), 0);
              const maxCount = Math.max(...chartData.map((u) => u.count || 0), 1);
              const topUser = chartData[0];
              const avgTeamScore = (() => {
                const scored = chartData.filter((u) => typeof u.avgScore === 'number');
                if (!scored.length) return null;
                return scored.reduce((s, u) => s + u.avgScore, 0) / scored.length;
              })();
              const scoreTone = (avg) => {
                if (typeof avg !== 'number') return 'neutral';
                if (avg < 40) return 'low';
                if (avg < 60) return 'mid';
                return 'high';
              };
              const barFill = (avg) => {
                if (typeof avg === 'number') {
                  if (avg < 40) return 'url(#barGradRed)';
                  if (avg < 60) return 'url(#barGradAmber)';
                }
                return 'url(#barGradGreen)';
              };

              const chips = (
                <div className="metrics-graph-chips">
                  <span className="metrics-chip">
                    <strong>{chartData.length}</strong> consultants
                  </span>
                  <span className="metrics-chip">
                    <strong>{totalComp}</strong> completions
                  </span>
                  {avgTeamScore != null && (
                    <span className="metrics-chip">
                      Team avg <strong>{Math.round(avgTeamScore)}%</strong>
                    </span>
                  )}
                  {topUser && (
                    <span className="metrics-chip metrics-chip-accent">
                      Lead: <strong>{topUser.name}</strong>
                    </span>
                  )}
                </div>
              );

              if (metricsView === 'pulse') {
                return (
                  <div className="dashboard-card metrics-graph-card pulse-board-card dash-field-in dash-delay-4">
                    <div className="metrics-graph-header">
                      <div>
                        <button
                          type="button"
                          className="metrics-back-btn"
                          onClick={() => setMetricsView('overview')}
                        >
                          <ChevronLeft size={16} />
                          Overall graph
                        </button>
                        <h3>Team Pulse</h3>
                        <p className="metrics-graph-sub">Completions &amp; avg score by consultant</p>
                      </div>
                      {chips}
                    </div>

                    <div className="pulse-board">
                      {chartData.map((entry, i) => {
                        const widthPct = Math.max(8, Math.round(((entry.count || 0) / maxCount) * 100));
                        const tone = scoreTone(entry.avgScore);
                        const score = typeof entry.avgScore === 'number' ? Math.round(entry.avgScore) : null;
                        const ring = 2 * Math.PI * 16;
                        const ringOffset = score == null ? ring : ring - (score / 100) * ring;
                        return (
                          <div
                            key={entry.fullEmail || i}
                            className={`pulse-row tone-${tone} ${i === 0 ? 'is-lead' : ''}`}
                            style={{ '--pulse-delay': `${i * 70}ms`, '--bar-width': `${widthPct}%` }}
                            title={`${entry.fullEmail} · ${entry.count} completions · ${score != null ? `${score}%` : 'no score'}`}
                          >
                            <div className="pulse-rank">{i + 1}</div>
                            <div className="pulse-avatar" aria-hidden="true">{entry.initial}</div>
                            <div className="pulse-main">
                              <div className="pulse-meta">
                                <span className="pulse-name">{entry.name}</span>
                                <span className="pulse-count">{entry.count} <em>done</em></span>
                              </div>
                              <div className="pulse-track">
                                <div className="pulse-bar" />
                                <div className="pulse-bar-glow" />
                              </div>
                            </div>
                            <div className="pulse-score" aria-label={score != null ? `Average score ${score}%` : 'No score'}>
                              <svg viewBox="0 0 40 40" className="pulse-ring">
                                <circle className="pulse-ring-bg" cx="20" cy="20" r="16" />
                                <circle
                                  className="pulse-ring-fg"
                                  cx="20"
                                  cy="20"
                                  r="16"
                                  style={{ strokeDasharray: ring, strokeDashoffset: ringOffset }}
                                />
                              </svg>
                              <span className="pulse-score-val">{score != null ? `${score}%` : '—'}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="metrics-legend">
                      <span><i className="lg-green" /> Avg score ≥ 60%</span>
                      <span><i className="lg-amber" /> 40–59%</span>
                      <span><i className="lg-red" /> Below 40%</span>
                    </div>
                  </div>
                );
              }

              return (
                <div className="dashboard-card metrics-graph-card metrics-overview-card dash-field-in dash-delay-4">
                  <div className="metrics-graph-header">
                    <div>
                      <h3>Completions by Consultant</h3>
                      <p className="metrics-graph-sub">Overall activity — click the chart for Team Pulse</p>
                    </div>
                    {chips}
                  </div>

                  <div
                    className="metrics-overview-hit"
                    role="button"
                    tabIndex={0}
                    onClick={() => setMetricsView('pulse')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setMetricsView('pulse');
                      }
                    }}
                    aria-label="Open Team Pulse detail view"
                  >
                    <div className="metrics-graph">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          key={`dash-bars-${chartData.map((d) => d.fullEmail).join('|')}`}
                          data={chartData}
                          margin={{ top: 24, right: 12, left: 4, bottom: 56 }}
                          barCategoryGap="18%"
                        >
                          <defs>
                            <linearGradient id="barGradGreen" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#2997ff" stopOpacity={1} />
                              <stop offset="100%" stopColor="#0071e3" stopOpacity={1} />
                            </linearGradient>
                            <linearGradient id="barGradAmber" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#fbbf24" stopOpacity={1} />
                              <stop offset="100%" stopColor="#d97706" stopOpacity={1} />
                            </linearGradient>
                            <linearGradient id="barGradRed" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#f87171" stopOpacity={1} />
                              <stop offset="100%" stopColor="#dc2626" stopOpacity={1} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="4 6" stroke="#e6ede9" vertical={false} />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: '#64756d', fontWeight: 600 }}
                            angle={-30}
                            textAnchor="end"
                            height={60}
                            interval={0}
                            axisLine={{ stroke: '#d8e3dd' }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#64756d' }}
                            allowDecimals={false}
                            axisLine={false}
                            tickLine={false}
                            width={36}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(18, 96, 71, 0.06)', radius: 8 }}
                            content={({ active, payload }) => {
                              if (!active || !payload?.[0]) return null;
                              const p = payload[0].payload;
                              const score = typeof p.avgScore === 'number' ? p.avgScore : null;
                              return (
                                <div className="metrics-tooltip">
                                  <div className="metrics-tooltip-name">{p.fullEmail}</div>
                                  <div className="metrics-tooltip-row">
                                    <span>Completions</span>
                                    <strong>{p.count}</strong>
                                  </div>
                                  <div className="metrics-tooltip-row">
                                    <span>Avg Score</span>
                                    <strong className={score != null && score < 60 ? 'low' : 'ok'}>
                                      {score != null ? `${Math.round(score)}%` : '—'}
                                    </strong>
                                  </div>
                                  <div className="metrics-tooltip-hint">Click for Team Pulse</div>
                                </div>
                              );
                            }}
                          />
                          <Bar
                            dataKey="count"
                            name="Completions"
                            radius={[10, 10, 4, 4]}
                            maxBarSize={56}
                            isAnimationActive
                            animationBegin={80}
                            animationDuration={1100}
                            animationEasing="ease-out"
                          >
                            {chartData.map((entry, i) => (
                              <Cell key={`cell-${i}`} fill={barFill(entry.avgScore)} />
                            ))}
                            <LabelList
                              dataKey="count"
                              position="top"
                              style={{ fill: '#1d1d1f', fontSize: 11, fontWeight: 700 }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <span className="metrics-drill-hint">Click to open Team Pulse →</span>
                  </div>

                  <div className="metrics-legend">
                    <span><i className="lg-green" /> Avg score ≥ 60%</span>
                    <span><i className="lg-amber" /> 40–59%</span>
                    <span><i className="lg-red" /> Below 40%</span>
                  </div>
                </div>
              );
            })()}

          </>
        );

      case 'quiz':
        return (
          <>
            <div className="section-header">
              <h1>Quiz</h1>
              <p>{isAdmin ? 'Upload, create sessions, and take assigned quizzes' : 'Quizzes assigned to you—ready to take'}</p>
            </div>

            {/* Admin only: Quiz Management */}
            {isAdmin && (
              <div className="quiz-management-row">
                <div className="dashboard-card quiz-action-card">
                  <div className="card-header">
                    <h3>Create Quiz Session</h3>
                    <FileText className="card-icon" size={24} />
                  </div>
                  <CreateQuizSession refreshTrigger={uploadRefreshTrigger} onSessionCreated={() => { handleUploadSuccess({ path: '', downloadUrl: '' }); loadQuizData(); }} />
                </div>
              </div>
            )}

            {/* Quizzes Ready to Take - both admin and consultant */}
            {(() => {
            const readyQuizzes = isDemoMode && !isAdmin ? DEMO_READY_QUIZZES : quizSessions;
            return (
            <div className="quiz-ready-section">
              <h3 className="quiz-ready-title">Quizzes Ready to Take</h3>
              {quizLoading ? (
                <div className="quiz-ready-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading your quizzes...</p>
                </div>
              ) : readyQuizzes.length === 0 ? (
                <div className="quiz-ready-empty">
                  <FileText className="empty-icon" size={48} />
                  <h4>No quizzes assigned yet</h4>
                  <p>{isAdmin ? 'Create a quiz session and assign users to get started.' : 'Your admin will assign quizzes to you. Check back later.'}</p>
                </div>
              ) : (
                <div className="quiz-ready-grid">
                  {readyQuizzes.map((session) => (
                    <div key={session.id} className="quiz-session-card" onClick={() => handleStartQuiz(session)}>
                      <div className="quiz-session-card-icon">
                        <FileText size={28} strokeWidth={1.5} />
                      </div>
                      <h4 className="quiz-session-card-title">{session.title || session.quizFileName || 'Quiz'}</h4>
                      <button type="button" className="quiz-session-start-btn">
                        <Play size={16} />
                        Start Quiz
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
            })()}

            {/* Admin only: Created Sessions (all sessions created by admin) */}
            {isAdmin && adminSessions.length > 0 && (
              <div className="quiz-history-section created-sessions-section">
                <h3 className="quiz-history-title">Created Sessions</h3>
                <div className="quiz-history-list">
                  {adminSessions.map((session) => (
                    <div key={session.id} className="history-item session-item">
                      <div className="history-info">
                        <span className="file-name">{session.title || session.quizFileName || 'Quiz'}</span>
                        <span className="upload-time">
                          {session.assignedUserEmails?.length || 0} participant(s)
                          {session.createdAt?.toDate?.() && ` · ${session.createdAt.toDate().toLocaleDateString()}`}
                        </span>
                      </div>
                      <div className="history-actions">
                        <button type="button" className="view-btn" onClick={() => handleStartQuiz(session)}>
                          <Play size={14} style={{ marginRight: 6 }} />
                          Start Quiz
                        </button>
                        <button
                          type="button"
                          className="delete-session-btn"
                          onClick={(e) => { e.stopPropagation(); setDeleteSessionConfirm(session); }}
                        >
                          <Trash2 size={14} style={{ marginRight: 6 }} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {deleteSessionConfirm && (
              <div className="confirm-overlay" onClick={() => setDeleteSessionConfirm(null)}>
                <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                  <h3>Delete session?</h3>
                  <p>
                    Delete &quot;{deleteSessionConfirm.title || deleteSessionConfirm.quizFileName || 'Quiz'}&quot;? 
                    This will remove the session. Assigned users will no longer see it.
                  </p>
                  <div className="confirm-actions">
                    <button type="button" className="confirm-btn cancel-btn" onClick={() => setDeleteSessionConfirm(null)}>Cancel</button>
                    <button type="button" className="confirm-btn confirm-primary" onClick={() => handleDeleteSession(deleteSessionConfirm.id)}>Delete</button>
                  </div>
                </div>
              </div>
            )}

            {/* Admin only: Quiz History (uploaded files) */}
            {isAdmin && quizFiles.length > 0 && (
              <div className="quiz-history-section">
                <h3 className="quiz-history-title">Quiz History</h3>
                <div className="quiz-history-list">
                  {quizFiles.map((file) => (
                    <div key={file.id} className="history-item">
                      <div className="history-info">
                        <span className="file-name">{file.fileName || file.path?.split('/').pop()}</span>
                        <span className="upload-time">
                          {file.uploadedAt?.toDate?.() ? file.uploadedAt.toDate().toLocaleDateString() : ''}
                        </span>
                      </div>
                      <div className="history-actions">
                        <button type="button" className="view-btn" onClick={() => window.open(file.downloadUrl, '_blank')}>
                          <ExternalLink size={14} style={{ marginRight: '6px' }} />
                          View
                        </button>
                        <button
                          type="button"
                          className="view-btn"
                          onClick={() => {
                            window.location.href = buildQuizPlayerUrl({
                              quizFileUrl: file.downloadUrl,
                              durationMinutes: file.durationMinutes,
                            });
                          }}
                        >
                          <Play size={14} style={{ marginRight: '6px' }} />
                          Start Quiz
                        </button>
                        <button
                          type="button"
                          className="delete-session-btn"
                          onClick={(e) => { e.stopPropagation(); setDeleteQuizFileConfirm(file); }}
                        >
                          <Trash2 size={14} style={{ marginRight: '6px' }} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {deleteQuizFileConfirm && (
              <div className="confirm-overlay" onClick={() => setDeleteQuizFileConfirm(null)}>
                <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                  <h3>Delete quiz file from history?</h3>
                  <p>
                    Remove &quot;{deleteQuizFileConfirm.fileName || deleteQuizFileConfirm.path?.split('/').pop() || 'Quiz'}&quot; from Quiz History?
                    The file will no longer appear here.
                  </p>
                  <div className="confirm-actions">
                    <button type="button" className="confirm-btn cancel-btn" onClick={() => setDeleteQuizFileConfirm(null)}>Cancel</button>
                    <button type="button" className="confirm-btn confirm-primary" onClick={() => handleDeleteQuizFile(deleteQuizFileConfirm.id)}>Delete</button>
                  </div>
                </div>
              </div>
            )}
          </>
        );

      case 'users':
        if (roleLoading) {
          return (
            <div className="section-header">
              <h1>User Management</h1>
              <p>Loading access...</p>
              <div className="loading-spinner"></div>
            </div>
          );
        }
        if (!modules.includes('users')) {
          return renderSection('dashboard'); // Redirect handled by useEffect
        }
        return (
          <>
            <div className="section-header">
              <h1>User Management</h1>
              <p>Manage users and roles (Firestore)</p>
            </div>
            <div className="dashboard-card">
              <UserManagement />
            </div>
          </>
        );

      case 'reports':
        if (roleLoading) {
          return (
            <div className="section-header">
              <h1>Quiz Reports</h1>
              <p>Loading...</p>
              <div className="loading-spinner"></div>
            </div>
          );
        }
        if (!modules.includes('reports')) {
          return renderSection('dashboard');
        }
        return (
          <>
            <div className="section-header">
              <h1>Quiz Reports</h1>
              <p>{isAdmin ? 'Quiz completion reports — view all consultants' : 'Your quiz completion reports'}</p>
            </div>
            <Reports userEmail={user?.email} isAdmin={isAdmin} />
          </>
        );

      case 'profile':
        return (
          <div className="dashboard-card">
            <Profile isAdmin={isAdmin} />
          </div>
        );

      case 'calendar':
        if (role === 'consultant' || !modules.includes('calendar')) {
          return null;
        }
        // Admin: full Diologic Scheduler. Consultant: own booking details only.
        if (isAdmin) return <DiologicSchedulerEmbed />;
        return <ConsultantBookings user={user} />;

      case 'timesheets':
        return (
          <TimesheetSection userEmail={user?.email} isAdmin={role === 'admin'} />
        );

      default:
        return null;
    }
  };

  return (
    <div className={`admin-dashboard-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <SidebarNavigation
        activeSection={activeSection}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={setIsSidebarCollapsed}
        allowedModules={modules}
        role={role}
      />
      <TopBar onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
      
      <main className="admin-main-content">
        <div className="content-wrapper">
          {renderSection()}
        </div>
      </main>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
